'use client'

import { StatsigClient } from '@statsig/js-client'
import { StatsigUser } from '@statsig/react-bindings'
import { useEffect, useRef } from 'react'

import { QUINCE_CONSENT_EVENT, QuinceConsentGrantedDetail } from '@src/telemetry/integrations/trustarc/trustarc.type'

const requiresConsent = (statsigUser: StatsigUser): boolean => 'hasConsented' in (statsigUser.custom ?? {})

/**
 * When the SSR-built StatsigUser carries a `hasConsented` flag (set by
 * `buildStatsigCustom` for consent-required locales), this hook keeps the
 * client-side Statsig user in sync with the cookie state across the three
 * TrustArc paths:
 *
 *   - Banner accept  → `quince:consent-granted` with `isFunctional: true`
 *                       dispatched; we flip `hasConsented` → true.
 *   - Banner reject  → same event with `isFunctional: false`; we flip
 *                       `hasConsented` → false (no reload on this path,
 *                       so without sync the cookie and Statsig user diverge).
 *   - Modal (any choice) → event suppressed by TrustArc's `isModalPath`
 *                       guard; modal-close mutation observer reloads the
 *                       page (UK only), so SSR rebuilds with the new cookie.
 *
 * `updateUserAsync` is idempotent for same-value updates, and the dispatcher
 * (`fireCookiePreferenceSaved`) already debounces duplicate fires within 500ms,
 * so we don't gate on a "synced" ref — the listener stays attached for the
 * lifetime of the provider to cover grant→revoke (or revoke→grant) sequences
 * within one page.
 */
export const useStatsigConsentSync = (client: StatsigClient, statsigUser: StatsigUser): void => {
  const userRef = useRef(statsigUser)
  userRef.current = statsigUser

  const consentRequired = requiresConsent(statsigUser)

  useEffect(() => {
    if (!consentRequired) return

    const handleConsentResolved = (event: Event) => {
      const { isFunctional } = (event as CustomEvent<QuinceConsentGrantedDetail>).detail
      const latest = userRef.current
      void client
        .updateUserAsync({
          ...latest,
          custom: { ...(latest.custom ?? {}), hasConsented: isFunctional },
        })
        .catch((error: unknown) => {
          // Fail closed: leave the user on the previous bucket if the update fails.
          console.error('Statsig consent sync failed', error)
        })
    }

    window.addEventListener(QUINCE_CONSENT_EVENT.GRANTED, handleConsentResolved)
    return () => {
      window.removeEventListener(QUINCE_CONSENT_EVENT.GRANTED, handleConsentResolved)
    }
  }, [client, consentRequired])
}
